import React, { forwardRef } from 'react';
import { Popover } from 'react-bootstrap';

const COLORS = {
  pendente:  '#f57c00',
  concluido: '#2e7d32',
  anulado:   '#d32f2f',
  neutral:   '#475569',
};

const toneCfg = {
  ok:      { bg: '#e8f5e9', bd: '#c8e6c9', title: COLORS.concluido },
  warn:    { bg: '#fff8e1', bd: '#ffe0b2', title: COLORS.pendente },
  danger:  { bg: '#fdecea', bd: '#ffcdd2', title: COLORS.anulado },
  neutral: { bg: '#f1f5f9', bd: '#e2e8f0', title: '#0f172a' },
};

const pick = (obj, keys) => {
  for (const k of keys) if (obj && obj[k] != null) return obj[k];
  return undefined;
};

const parseDateMaybe = (v) => {
  if (!v) return null;
  const d = new Date(v);
  return Number.isFinite(d.getTime()) ? d : null;
};

const alpha = (hex, a = 1) => {
  const h = (hex || '').replace('#', '');
  const n = parseInt(h.length === 3 ? h.split('').map(c => c + c).join('') : h, 16);
  const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
  return `rgba(${r}, ${g}, ${b}, ${a})`;
};

const fmtHMS = (seconds) => {
  const n = Number(seconds);
  if (!Number.isFinite(n) || n < 0) return '—';
  const s = Math.floor(n);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return h ? `${h}h ${m}m` : `${m}m`;
};

const getDurationText = (v = {}) => {
  const readyText = pick(v, ['duracao', 'duration', 'tempo']);
  if (readyText) return String(readyText);

  const sec = pick(v, ['adjustment_timer', 'adjustmentTimer', 'timerSeconds']);
  if (Number.isFinite(sec)) return fmtHMS(sec);

  const created  = parseDateMaybe(pick(v, ['created_at', 'createdAt', 'inicioRaw', 'inicio']));
  const finished = parseDateMaybe(pick(v, ['finished_at', 'finishedAt', 'fimRaw', 'fim']));
  const status   = String(pick(v, ['status_slug', 'statusSlug']) || '').toUpperCase();

  if (created) {
    const end = finished || (status === 'PENDING' ? new Date() : null);
    if (end) {
      const diffS = (end - created) / 1000;
      if (Number.isFinite(diffS)) return fmtHMS(diffS);
    }
  }
  return '—';
};

function VoltaStatusChip({ status, time }) {
  const st = String(status || '').toUpperCase();

  const STATUS_UI = {
    PENDING:  { label: 'Pendente',  base: COLORS.pendente,  bg: '#FFF3E0' },
    FINISHED:{ label: 'Concluído',  base: COLORS.concluido, bg: '#EAF7EA' },
    ANNULLED:{ label: 'Anulado',    base: COLORS.anulado,   bg: '#FDECEA' },
  };
  const cfg = STATUS_UI[st] || { label: st || '—', base: COLORS.neutral, bg: '#F1F5F9' };

  const fg = cfg.base;
  const bd = alpha(cfg.base, 0.35);

  const iconCommon = { size: 14, stroke: fg, fill: alpha(fg, 0.15), strokeWidth: 2 };

  const icon = st === 'PENDING' ? (
        <svg width={iconCommon.size} height={iconCommon.size} viewBox="0 0 24 24" aria-hidden>
          <circle cx="12" cy="12" r="10" fill={iconCommon.fill}/>
          <path d="M12 7v5l3 2" stroke={iconCommon.stroke} strokeWidth={iconCommon.strokeWidth} fill="none" strokeLinecap="round"/>
          <circle cx="12" cy="12" r="9" stroke={iconCommon.stroke} strokeWidth={iconCommon.strokeWidth} fill="none"/>
        </svg>
      ) : st === 'FINISHED' ? (
        <svg width={iconCommon.size} height={iconCommon.size} viewBox="0 0 24 24" aria-hidden>
          <circle cx="12" cy="12" r="10" fill={iconCommon.fill}/>
          <path d="M7 12l3 3 7-7" stroke={iconCommon.stroke} strokeWidth={iconCommon.strokeWidth} fill="none" strokeLinecap="round"/>
          <circle cx="12" cy="12" r="9" stroke={iconCommon.stroke} strokeWidth={iconCommon.strokeWidth} fill="none"/>
        </svg>
      ) : st === 'ANNULLED' ? (
        <svg width={iconCommon.size} height={iconCommon.size} viewBox="0 0 24 24" aria-hidden>
          <circle cx="12" cy="12" r="10" fill={iconCommon.fill}/>
          <path d="M9 9l6 6M15 9l-6 6" stroke={iconCommon.stroke} strokeWidth={iconCommon.strokeWidth} strokeLinecap="round"/>
          <circle cx="12" cy="12" r="9" stroke={iconCommon.stroke} strokeWidth={iconCommon.strokeWidth} fill="none"/>
        </svg>
      ) : null;

  const displayTime = (() => {
    const t = String(time ?? '').trim();
    return t && !['-', '—'].includes(t) ? t : '';
  })();

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        padding: '4px 10px',
        borderRadius: 999,
        background: cfg.bg,
        border: `1px solid ${bd}`,
        color: fg,
        fontSize: 12,
        fontWeight: 700,
        lineHeight: 1,
        whiteSpace: 'nowrap',
      }}
      title={`${cfg.label}${displayTime ? ` • ${displayTime}` : ''}`}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        {icon}
        {displayTime && <span style={{ fontWeight: 600 }}>{displayTime}</span>}
      </span>

      <span aria-hidden style={{ width: 1, height: 14, background: bd, display: 'inline-block' }} />
      <span>{cfg.label}</span>
    </span>
  );
}
const MAX_LIST_HEIGHT = 360;

const VoltasPopover = forwardRef(function VoltasPopover(
  { historico = [], popoverId = 'voltas-popover', style, onEntered, onExited, requestClose, ...rest },
  ref
) {
  const HeaderComp = (Popover && (Popover.Header || Popover.Title)) || 'div';
  const BodyComp   = (Popover && (Popover.Body   || Popover.Content)) || 'div';

  const safeHistorico = Array.isArray(historico) ? historico : [];

  const renderVoltaCard = (v, i) => {
    const tone      = toneCfg[v?.tone] || toneCfg.neutral;

    const inicio    = pick(v, ['inicio', 'inicioRaw', 'created_at', 'createdAt']) || '—';
    const fimRaw    = pick(v, ['fim', 'fimRaw', 'finished_at', 'finishedAt']);
    const status    = String(pick(v, ['status_slug', 'statusSlug']) || '').toUpperCase();

    const fim = (() => {
      const candidate = (fimRaw ?? (status === 'PENDING' ? 'em aberto' : '')).toString().trim();
      return candidate && !['-', '—'].includes(candidate) ? candidate : '';
    })();

    const durText   = getDurationText(v);

    const origem    = v?.origemSetor  ?? v?.from_group?.name ?? '—';
    const destino   = v?.destinoSetor ?? v?.to_group?.name   ?? '—';
    const criadoPor = v?.criadoPor    ?? v?.user_created?.name ?? v?.user_created_name ?? '—';
    const resolvidoPor = v?.resolvidoPor ?? v?.user_updated?.name ?? v?.user_updated_name ?? (status === 'PENDING' ? '—' : '—');

    return (
      <div
        key={i}
        style={{
          border: `1px solid ${tone.bd}`,
          borderRadius: 10,
          padding: 14,
          background: '#ffffff',
          marginBottom: 10,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            marginBottom: 8,
            minWidth: 0,
            justifyContent: 'space-between',
          }}
        >
          <div
            style={{
              fontWeight: 600,
              fontSize: 14,
              color: '#0f172a',
              whiteSpace: 'nowrap',
              minWidth: 0,
              flex: 1,
            }}
          >
            Volta #{i + 1}
          </div>

          <div style={{ flex: 'none', whiteSpace: 'nowrap', marginLeft: 'auto' }}>
            <VoltaStatusChip status={status} time={durText} />
          </div>
        </div>

        <div style={{ marginBottom: 12 }}>
          <div style={{ height: 1, background: '#e2e8f0', marginBottom: 8 }} />

          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div
              style={{
                color: '#64748b',
                fontSize: 12,
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                maxWidth: '100%',
                marginLeft: 'auto', 
                textAlign: 'right',
              }}
              title={fim ? `${inicio} — ${fim}` : `${inicio}`}
            >
              {inicio} {fim ? <>&nbsp;—&nbsp;{fim}</> : null}
            </div>
          </div>
          
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
              columnGap: 16,
              rowGap: 6,
              fontSize: 13,
              color: '#475569'
            }}
          >
            <div><strong>Setor de Origem:</strong> {origem}</div>
            <div><strong>Setor de Destino:</strong> {destino}</div>
          </div>
          <div style={{ display: 'grid', fontSize: 13, color: '#475569' }}>
            <div><strong>Criado por:</strong> {criadoPor}</div>
            <div><strong>Resolvido por:</strong> {resolvidoPor}</div>
          </div>
        </div>

        <div
          style={{
            border: `1px solid ${tone.bd}`,
            background: tone.bg,
            borderRadius: 8,
            padding: 12,
          }}
        >
          <div style={{ fontWeight: 700, color: tone.title, marginBottom: 4 }}>Motivo:</div>
          <div style={{ marginBottom: 8 }}>{pick(v, ['motivo', 'reason_description']) ?? '—'}</div>

          {pick(v, ['descricao', 'description']) && (
            <>
              <div style={{ fontWeight: 700, color: tone.title, marginBottom: 4 }}>Descrição:</div>
              <div style={{ marginBottom: 8 }}>{pick(v, ['descricao', 'description'])}</div>
            </>
          )}

          {pick(v, ['resolucao', 'finished_description']) && (
            <>
              <div style={{ fontWeight: 700, color: tone.title, marginBottom: 4 }}>Resolução:</div>
              <div>{pick(v, ['resolucao', 'finished_description'])}</div>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <Popover
      id={popoverId}
      ref={ref}
      style={{
        width: 520,
        maxWidth: '90vw',
        boxShadow: '0 6px 24px rgba(0,0,0,.18)',
        zIndex: 3000,
        ...style,
      }}
      onEntered={onEntered}
      onExited={onExited}
      {...rest}
    >
      <HeaderComp as="h3" style={{ fontSize: 16, fontWeight: 700, position: 'relative', paddingRight: 28 }}>
        Histórico de Voltas
        <button
          type="button"
          aria-label="Fechar"
          onClick={() => requestClose && requestClose()}
          style={{
            position: 'absolute', right: 8, top: 6, border: 'none', background: 'transparent',
            fontSize: 18, lineHeight: 1, cursor: 'pointer', color: '#64748b'
          }}
        >
          ×
        </button>
      </HeaderComp>

      <BodyComp
        style={{
          paddingTop: 8,
          maxHeight: MAX_LIST_HEIGHT,
          overflowY: 'auto',
          overflowX: 'hidden',
          paddingRight: 6,
          overscrollBehavior: 'contain',
        }}
      >
        {safeHistorico.length === 0
          ? <div style={{ fontSize: 13, color: '#475569' }}>Sem registros de volta.</div>
          : safeHistorico.map(renderVoltaCard)}
      </BodyComp>
    </Popover>
  );
});

export default VoltasPopover;